import React, { useState, useRef, useEffect } from 'react';
import { Shield, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { authApi } from '../../api/auth/authApi';
import { useCurrentUser } from '../../hooks/useCurrentUser';

export type AuthenticationContext = 'form' | 'registration' | 'survey' | 'custom';

interface AuthenticationRequiredProps {
  context: AuthenticationContext;
  title?: string;
  message?: string;
  primaryColor: string;
  onAuthenticated: () => void;
  // Optional props for customization
  iconComponent?: React.ReactNode;
  signInButtonText?: string;
  createAccountButtonText?: string;
  signInFormTitle?: string;
  signInFormMessage?: string;
  // For form context compatibility
  entityName?: string;
  // For order persistence
  orderToken?: string;
  participantTypeSlug?: string;
}

const defaultMessages: Record<AuthenticationContext, { title: string; message: string }> = {
  form: {
    title: 'Account Required',
    message: 'This form requires you to have an account. Please sign in or create an account to continue with your submission.',
  },
  registration: {
    title: 'Account Required',
    message: 'Event registration requires you to have an account. Please sign in or create an account to continue with registration.',
  },
  survey: {
    title: 'Account Required',
    message: 'This survey requires you to have an account. Please sign in or create an account to continue.',
  },
  custom: {
    title: 'Account Required',
    message: 'Please sign in or create an account to continue.',
  },
};

export const AuthenticationRequired: React.FC<AuthenticationRequiredProps> = ({
  context,
  title,
  message,
  primaryColor,
  onAuthenticated,
  iconComponent,
  signInButtonText = 'Sign In',
  createAccountButtonText = 'Create Account',
  signInFormTitle = 'Sign In to Continue',
  signInFormMessage,
  entityName,
  orderToken,
  participantTypeSlug,
}) => {
  const navigate = useNavigate();
  const { refetch: refetchUser } = useCurrentUser();
  const [showSignIn, setShowSignIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Store current location and order persistence info on mount
  useEffect(() => {
    const setupStoredLocation = async () => {
      // Build the return URL with authenticated parameter
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('authenticated', 'true');
      const returnPath = currentUrl.pathname + currentUrl.search;

      // Note: Since we're not using httponly, we can manage cookies entirely from JavaScript

      // Clear any existing stored_account_location cookies on client side
      // IMPORTANT: Only clear cookies for the current subdomain to prevent cross-team issues
      const hostname = window.location.hostname;

      // Clear with js-cookie for current subdomain only
      Cookies.remove('stored_account_location', { path: '/' });
      Cookies.remove('stored_account_location', { domain: hostname, path: '/' });

      // If we have order info, store it in sessionStorage for recovery
      if (orderToken && participantTypeSlug && context === 'registration') {
        try {
          const eventSlug = currentUrl.pathname.split('/')[1]; // Extract event slug from /{eventSlug}/register
          const orderPersistenceData = {
            token: orderToken,
            participantTypeSlug,
            eventSlug,
            timestamp: Date.now()
          };

          sessionStorage.setItem('pending_order', JSON.stringify(orderPersistenceData));
          localStorage.setItem('pending_order', JSON.stringify(orderPersistenceData));
        } catch (error) {
          console.error('[AuthenticationRequired] Failed to store order persistence info:', error);
        }
      }

      // Build cookie data with order recovery info
      let cookieData = returnPath;
      if (orderToken && participantTypeSlug && context === 'registration') {
        cookieData = JSON.stringify({
          returnPath,
          orderToken,
          participantTypeSlug
        });
      }

      // Set fresh cookie with current location and order info
      // IMPORTANT: Set cookie for current subdomain only, not cross-subdomain
      // This prevents users from being redirected across different teams
      Cookies.set('stored_account_location', cookieData, {
        // No domain specified = cookie will be set for current subdomain only
        path: '/',
        expires: 1/24, // 1 hour
        sameSite: 'Lax'
      });

      console.log('[AuthenticationRequired] Set stored_account_location cookie for subdomain:', hostname, 'path:', returnPath);
    };

    setupStoredLocation();
  }, [orderToken, participantTypeSlug, context]);

  // Use provided title/message or defaults based on context
  const displayTitle = title || defaultMessages[context].title;
  const displayMessage = message || defaultMessages[context].message;
  const displaySignInMessage = signInFormMessage ||
    (context === 'form' ? `Please sign in to your account to submit this ${entityName || 'form'}.` :
     context === 'registration' ? 'Please sign in to your account to continue with registration.' :
     context === 'survey' ? 'Please sign in to your account to continue with this survey.' :
     'Please sign in to your account to continue.');

  const handleSignUp = () => {
    // Store the return URL in sessionStorage with authenticated parameter
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('authenticated', 'true');
    const returnUrl = currentUrl.pathname + currentUrl.search;
    sessionStorage.setItem('signUpReturnUrl', returnUrl);

    // Store order persistence info if available
    if (orderToken && participantTypeSlug && context === 'registration') {
      try {
        const eventSlug = currentUrl.pathname.split('/')[1];
        const orderPersistenceData = {
          token: orderToken,
          participantTypeSlug,
          eventSlug,
          timestamp: Date.now()
        };

        sessionStorage.setItem('pending_order', JSON.stringify(orderPersistenceData));
        localStorage.setItem('pending_order', JSON.stringify(orderPersistenceData));
      } catch (error) {
        console.error('[AuthenticationRequired] Failed to store order persistence info for sign-up:', error);
      }
    }

    // Also add redirect_to query parameter as fallback for users with blocked cookies
    // We need to properly encode the returnUrl to avoid double encoding issues
    const signUpUrl = new URL('/accounts/sign_up', window.location.origin);
    // Use encodeURIComponent to ensure the returnUrl is properly encoded as a query parameter value
    signUpUrl.searchParams.set('redirect_to', returnUrl);

    // Use window.location.href for sign-up to ensure proper session handling
    window.location.href = signUpUrl.toString();
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsValidating(true);
    setError(null);

    try {
      // Get the current subdomain
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      const subdomain = parts.length > 2 ? parts[0] : 'www';

      // First validate the password via API
      const validationResponse = await authApi.validatePassword({
        account: { email, password, subdomain }
      });

      if (validationResponse.status === 'success') {
        // Password is valid, now submit the form to establish the session
        setIsLoading(true);

        const form = formRef.current;
        if (form) {
          // Create a form that submits to the accounts sign-in path
          const hiddenForm = document.createElement('form');
          hiddenForm.method = 'POST';
          hiddenForm.action = '/accounts/sign_in';
          hiddenForm.style.display = 'none';

          // Add CSRF token
          const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
          if (csrfToken) {
            const csrfInput = document.createElement('input');
            csrfInput.type = 'hidden';
            csrfInput.name = 'authenticity_token';
            csrfInput.value = csrfToken;
            hiddenForm.appendChild(csrfInput);
          }

          // Add email
          const emailInput = document.createElement('input');
          emailInput.type = 'hidden';
          emailInput.name = 'account[email]';
          emailInput.value = email;
          hiddenForm.appendChild(emailInput);

          // Add password
          const passwordInput = document.createElement('input');
          passwordInput.type = 'hidden';
          passwordInput.name = 'account[password]';
          passwordInput.value = password;
          hiddenForm.appendChild(passwordInput);

          // Add subdomain
          const subdomainInput = document.createElement('input');
          subdomainInput.type = 'hidden';
          subdomainInput.name = 'account[subdomain]';
          subdomainInput.value = subdomain;
          hiddenForm.appendChild(subdomainInput);

          // Cookie is already set in useEffect, no need to set it again
          console.log('[AuthenticationRequired] Using stored_account_location cookie for redirect');

          // Append to body and submit
          document.body.appendChild(hiddenForm);
          hiddenForm.submit();
        } else {
          console.error('Form ref not found');
          setError('An error occurred. Please try again.');
          setIsValidating(false);
          setIsLoading(false);
        }
      } else {
        // Password validation failed
        setError(validationResponse.message || 'Invalid email or password');
        setIsValidating(false);
      }
    } catch (err) {
      console.error('Sign in error:', err);
      setError('An error occurred during sign in. Please try again.');
      setIsValidating(false);
    }
  };

  if (showSignIn) {
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-semibold text-gray-900">{signInFormTitle}</h2>
            <button
              onClick={() => setShowSignIn(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-gray-600 mb-6">
            {displaySignInMessage}
          </p>

          <form ref={formRef} onSubmit={handleSignIn} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
                required
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
                required
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isLoading || isValidating}
                className="flex-1 py-2.5 text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-70"
                style={{
                  backgroundColor: primaryColor,
                  '--tw-ring-color': primaryColor,
                } as React.CSSProperties}
              >
                {isValidating ? 'Validating...' : isLoading ? 'Signing in...' : 'Sign In'}
              </button>
              <button
                type="button"
                onClick={() => setShowSignIn(false)}
                className="px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </form>

          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                onClick={handleSignUp}
                className="font-medium hover:underline"
                style={{ color: primaryColor }}
              >
                Sign up
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg">
      <div className="p-8 text-center">
        {iconComponent || (
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <Shield className="h-8 w-8" style={{ color: primaryColor }} />
          </div>
        )}

        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          {displayTitle}
        </h2>

        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          {displayMessage}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-sm mx-auto">
          <button
            onClick={() => setShowSignIn(true)}
            className="px-6 py-2.5 text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{
              backgroundColor: primaryColor,
              '--tw-ring-color': primaryColor,
            } as React.CSSProperties}
          >
            {signInButtonText}
          </button>

          <button
            onClick={handleSignUp}
            className="px-6 py-2.5 border-2 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{
              borderColor: primaryColor,
              color: primaryColor,
              '--tw-ring-color': primaryColor,
            } as React.CSSProperties}
          >
            {createAccountButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};